import { AvailableEntity, FilterCondition } from '../types'
import {
  getDataClassByEntityName,
  getDataClassByReference,
} from './getReferenceDataClass'
import { SchemaAttributes } from '@/Data/types'
import { ReferenceFilter } from '@/Data/typesV2'

type DataClass = ReturnType<typeof getDataClassByEntityName>

export function resolveEntityDataClass(
  conditionEntity: string,
  baseEntityName: string,
  condition: FilterCondition,
  availableEntities: AvailableEntity[],
  baseSchemaAttributes: SchemaAttributes | null | undefined,
): DataClass {
  const isMainEntity = conditionEntity === baseEntityName

  if (isMainEntity) {
    return getDataClassByEntityName(conditionEntity)
  }

  const selectedEntity = findSelectedEntity(
    conditionEntity,
    condition,
    availableEntities,
    baseSchemaAttributes,
  )

  if (!selectedEntity) {
    return getDataClassByEntityName(conditionEntity)
  }

  if (isCollectionRelation(selectedEntity)) {
    return getDataClassByEntityName(conditionEntity)
  }

  return resolveReferenceDataClass(selectedEntity, baseSchemaAttributes)
}

function findSelectedEntity(
  conditionEntity: string,
  condition: FilterCondition,
  availableEntities: AvailableEntity[],
  baseSchemaAttributes: SchemaAttributes | null | undefined,
): AvailableEntity | undefined {
  let selectedEntity = availableEntities.find(
    (e) =>
      e.value === conditionEntity &&
      e.referenceField === condition.referenceField,
  )

  if (!selectedEntity) {
    selectedEntity = availableEntities.find((e) => e.value === conditionEntity)
  }

  if (!selectedEntity && condition.referenceField) {
    selectedEntity = findEntityFromReferenceField(
      condition.referenceField,
      baseSchemaAttributes,
      availableEntities,
    )
  }

  return selectedEntity
}

function findEntityFromReferenceField(
  referenceField: string,
  baseSchemaAttributes: SchemaAttributes | null | undefined,
  availableEntities: AvailableEntity[],
): AvailableEntity | undefined {
  const refFieldAttr = baseSchemaAttributes?.[referenceField]
  if (!refFieldAttr?.referenceTo) return undefined

  const actualEntity = refFieldAttr.referenceTo.toLowerCase()
  return availableEntities.find(
    (e) => e.value === actualEntity && e.referenceField === referenceField,
  )
}

function isCollectionRelation(entity: AvailableEntity): boolean {
  return (
    entity.relationType === 'one-to-many' ||
    entity.relationType === 'many-to-many'
  )
}

function resolveReferenceDataClass(
  selectedEntity: AvailableEntity,
  baseSchemaAttributes: SchemaAttributes | null | undefined,
): DataClass {
  if (!selectedEntity.referenceField) {
    console.error(`No reference field found for entity ${selectedEntity.value}`)
    return null
  }

  const baseAttribute = baseSchemaAttributes?.[selectedEntity.referenceField]
  if (!baseAttribute?.referenceTo) {
    console.error(
      `No referenceTo found for reference field ${selectedEntity.referenceField}`,
    )
    return null
  }

  return getDataClassByReference(
    baseAttribute.referenceTo,
    baseAttribute.referenceFilter as ReferenceFilter | undefined,
  )
}
